import { Show, createSignal, createEffect, untrack, For } from "solid-js"
import type { UploadMetadata, CreateCategoryInput, PreparedSkillFile } from "./skill-market-types"
import type { MarketplaceSkill } from "./skill-market-types"
import { extractZipToSkillFiles } from "./skill-market-zip"

export function SkillMarketUpload(props: {
  show: boolean
  uploading: boolean
  onClose: () => void
  onUpload: (files: PreparedSkillFile[], metadata: UploadMetadata) => void
  title?: string
  prefillSkill?: MarketplaceSkill | null
  // 分类下拉选项（来自后端 GET /api/skill-market/categories，已剔除 "all"）；为空时回退硬编码 6 项
  categories?: { code: string; label: string }[]
  // 创建自定义分类（写入 skill_category 表）；成功后返回新分类，用于自动选中
  onCreateCategory?: (input: CreateCategoryInput) => Promise<{ code: string } | null>
  creatingCategory?: boolean
}) {
  // 归一化的待上传文件（文件夹与 zip 两条路径统一产出），替代原 File[] 信号
  const [preparedFiles, setPreparedFiles] = createSignal<PreparedSkillFile[]>([])
  // 上传来源模式：folder（选文件夹）/ zip（选 zip 压缩包），用切换按钮控制
  const [mode, setMode] = createSignal<"folder" | "zip">("folder")
  // zip 解压进行中态（async，期间禁用上传）
  const [extracting, setExtracting] = createSignal(false)
  const [name, setName] = createSignal("")
  const [displayName, setDisplayName] = createSignal("")
  const [description, setDescription] = createSignal("")
  const [category, setCategory] = createSignal("other")
  const [version, setVersion] = createSignal("1.0.0")
  const [author, setAuthor] = createSignal("")
  const [tags, setTags] = createSignal("")

  // 「新加分类」子弹框本地状态
  const [showAddCategory, setShowAddCategory] = createSignal(false)
  const [newCode, setNewCode] = createSignal("")
  const [newLabel, setNewLabel] = createSignal("")
  const [newSortOrder, setNewSortOrder] = createSignal("")

  // When the modal opens, prefill fields from the original skill (if provided).
  // This is used by the "更新版本" flow so users don't have to re-enter everything.
  createEffect(() => {
    if (!props.show) return
    const skill = props.prefillSkill
    if (!skill) return
    // untrack to avoid re-running when individual signals change
    untrack(() => {
      setName(skill.name ?? "")
      setDisplayName(skill.display_name ?? "")
      setDescription(skill.description ?? "")
      setCategory(skill.category ?? "other")
      setVersion(skill.version ?? "1.0.0")
      setAuthor(skill.author ?? "")
      setTags(skill.tags ? skill.tags.join(", ") : "")
    })
  })

  // 文件夹拾取（webkitdirectory）：按 webkitRelativePath + slice(1) 构造成
  // 归一化的 PreparedSkillFile[]（剥顶层文件夹名，保留嵌套目录如 scripts/foo.ts，与原 controller 逻辑等价）。
  async function handleFolderSelect(e: Event) {
    const input = e.target as HTMLInputElement
    if (!input.files || input.files.length === 0) return
    const fileArr = Array.from(input.files)
    const prepared: PreparedSkillFile[] = []
    for (const file of fileArr) {
      const relativePath = (file as any).webkitRelativePath || file.name
      const pathParts = relativePath.split("/")
      const skillRelativePath = pathParts.length > 1 ? pathParts.slice(1).join("/") : file.name
      const buf = await file.arrayBuffer()
      prepared.push({ path: skillRelativePath, bytes: new Uint8Array(buf) })
    }
    setPreparedFiles(prepared)
    if (!name()) {
      const firstFile = fileArr[0] as any
      if (firstFile.webkitRelativePath) {
        const folderName = firstFile.webkitRelativePath.split("/")[0]
        setName(folderName)
      }
    }
    input.value = ""
  }

  // zip 拾取：选单个 .zip → 浏览器端解压为 PreparedSkillFile[]（顶层文件夹已剥，保留嵌套结构）
  async function handleZipSelect(e: Event) {
    const input = e.target as HTMLInputElement
    if (!input.files || input.files.length === 0) return
    const zipFile = input.files[0]
    setExtracting(true)
    try {
      const prepared = await extractZipToSkillFiles(zipFile)
      setPreparedFiles(prepared)
      if (!name()) {
        // zip 文件名去掉 .zip 后缀作为技能名（与文件夹模式取文件夹名对齐）
        const base = zipFile.name.replace(/\.zip$/i, "")
        if (base) setName(base)
      }
    } catch {
      setPreparedFiles([])
    } finally {
      setExtracting(false)
      // 清空 input.value，使同一文件可重复选择（如解压失败后重选）
      input.value = ""
    }
  }

  // 切换来源模式：清空已选文件与名称，避免上一模式的自动填充名串到新模式
  function switchMode(next: "folder" | "zip") {
    if (mode() === next) return
    setMode(next)
    setPreparedFiles([])
    setName("")
  }

  function handleUpload() {
    if (preparedFiles().length === 0) return
    if (!name()) return

    const metadata: UploadMetadata = {
      name: name(),
      display_name: displayName() || undefined,
      description: description() || undefined,
      category: category(),
      version: version() || undefined,
      author: author() || undefined,
      tags: tags() ? tags().split(",").map((t) => t.trim()).filter(Boolean) : undefined,
    }
    props.onUpload(preparedFiles(), metadata)
  }

  // 「新加分类」作为 select 内固定 option（类似「全部技能」），仅作点击触发入口：
  // 选中时立即把 select 回退到当前分类（命令式 el.value=category()，避免被选中/回填），再开弹框。
  const NEW_CATEGORY_VALUE = "__new_category__"
  function openAddCategory() {
    setNewCode("")
    setNewLabel("")
    setNewSortOrder("")
    setShowAddCategory(true)
  }

  function handleCategoryChange(e: Event) {
    const el = e.currentTarget as HTMLSelectElement
    if (el.value === NEW_CATEGORY_VALUE) {
      el.value = category() // 命令式回退，不让「新加分类」成为选中值/回填
      openAddCategory()
      return
    }
    setCategory(el.value)
  }

  // 确认新加分类 → 调 onCreateCategory；成功后选中新分类并关闭子弹框
  async function handleCreateCategory() {
    const code = newCode().trim()
    const label = newLabel().trim()
    if (!code || !label) return
    const sortNum = newSortOrder().trim() === "" ? undefined : Number(newSortOrder())
    const created = await props.onCreateCategory?.({
      code,
      label,
      sort_order: Number.isNaN(sortNum) ? 0 : (sortNum ?? 0),
    })
    if (created) {
      setCategory(created.code)
      setShowAddCategory(false)
    }
  }

  return (
    <Show when={props.show}>
      <div class="skill-market-upload-overlay" onClick={props.onClose}>
        <div class="skill-market-upload-dialog" onClick={(e) => e.stopPropagation()}>
          <div class="skill-market-upload-header">
            <span class="skill-market-upload-title">{props.title ?? "上传技能"}</span>
            <button class="skill-market-upload-close" onClick={props.onClose}>✕</button>
          </div>

          <div style={{ display: "flex", "flex-direction": "column", gap: "16px" }}>
            <div class="skill-market-upload-field">
              {/* 来源模式切换：文件夹 / ZIP 压缩包（浏览器拾取器二选一，故用切换） */}
              <div style={{ display: "flex", gap: "8px", "margin-bottom": "8px" }}>
                <button
                  type="button"
                  class="skill-market-detail-btn-ghost"
                  classList={{ "skill-market-detail-btn-primary": mode() === "folder" }}
                  style={{ flex: "0 0 auto", padding: "4px 12px", cursor: "pointer" }}
                  onClick={() => switchMode("folder")}
                >
                  文件夹
                </button>
                <button
                  type="button"
                  class="skill-market-detail-btn-ghost"
                  classList={{ "skill-market-detail-btn-primary": mode() === "zip" }}
                  style={{ flex: "0 0 auto", padding: "4px 12px", cursor: "pointer" }}
                  onClick={() => switchMode("zip")}
                >
                  ZIP 压缩包
                </button>
              </div>

              <Show
                when={mode() === "folder"}
                fallback={
                  <>
                    <span class="skill-market-upload-label">选择 ZIP 压缩包（需包含 SKILL.md）</span>
                    <input
                      type="file"
                      accept=".zip"
                      class="skill-market-upload-input"
                      onChange={handleZipSelect}
                      disabled={extracting()}
                    />
                    <Show when={extracting()}>
                      <span class="skill-market-upload-hint">解压中...</span>
                    </Show>
                  </>
                }
              >
                <span class="skill-market-upload-label">选择技能文件夹（需包含 SKILL.md）</span>
                <input
                  type="file"
                  ref={(input) => input.setAttribute("webkitdirectory", "")}
                  multiple
                  class="skill-market-upload-input"
                  onChange={handleFolderSelect}
                />
              </Show>

              <Show when={preparedFiles().length > 0}>
                <span class="skill-market-upload-hint">已选择 {preparedFiles().length} 个文件</span>
              </Show>
            </div>

            <div style={{ display: "grid", "grid-template-columns": "1fr 1fr", gap: "12px" }}>
              <div class="skill-market-upload-field">
                <span class="skill-market-upload-label">技能名称 *</span>
                <input
                  type="text"
                  value={name()}
                  onInput={(e) => setName(e.currentTarget.value)}
                  placeholder="my-skill"
                  class="skill-market-upload-input"
                />
              </div>
              <div class="skill-market-upload-field">
                <span class="skill-market-upload-label">显示名称</span>
                <input
                  type="text"
                  value={displayName()}
                  onInput={(e) => setDisplayName(e.currentTarget.value)}
                  placeholder="我的技能"
                  class="skill-market-upload-input"
                />
              </div>
            </div>

            <div class="skill-market-upload-field">
              <span class="skill-market-upload-label">描述</span>
              <textarea
                value={description()}
                onInput={(e) => setDescription(e.currentTarget.value)}
                placeholder="技能描述..."
                rows={2}
                class="skill-market-upload-input"
                style={{ resize: "vertical" }}
              />
            </div>

            <div style={{ display: "grid", "grid-template-columns": "1fr 1fr 1fr", gap: "12px" }}>
              <div class="skill-market-upload-field">
                <span class="skill-market-upload-label">分类</span>
                <select
                  value={category()}
                  onChange={handleCategoryChange}
                  class="skill-market-upload-input"
                >
                  <Show
                    when={props.categories && props.categories.length > 0}
                    fallback={
                      <>
                        <option value="security-audit">安全审计</option>
                        <option value="vulnerability-scan">漏洞扫描</option>
                        <option value="compliance">合规检查</option>
                        <option value="incident-response">应急响应</option>
                        <option value="forensics">数字取证</option>
                        <option value="other">其他</option>
                        <option value={NEW_CATEGORY_VALUE}>--新加分类--</option>
                      </>
                    }
                  >
                    <For each={props.categories}>
                      {(cat) => <option value={cat.code}>{cat.label}</option>}
                    </For>
                    <option value={NEW_CATEGORY_VALUE}>--新加分类--</option>
                  </Show>
                </select>
              </div>
              <div class="skill-market-upload-field">
                <span class="skill-market-upload-label">版本</span>
                <input
                  type="text"
                  value={version()}
                  onInput={(e) => setVersion(e.currentTarget.value)}
                  placeholder="1.0.0"
                  class="skill-market-upload-input"
                />
              </div>
              <div class="skill-market-upload-field">
                <span class="skill-market-upload-label">作者</span>
                <input
                  type="text"
                  value={author()}
                  onInput={(e) => setAuthor(e.currentTarget.value)}
                  placeholder="作者"
                  class="skill-market-upload-input"
                />
              </div>
            </div>

            <div class="skill-market-upload-field">
              <span class="skill-market-upload-label">标签（逗号分隔）</span>
              <input
                type="text"
                value={tags()}
                onInput={(e) => setTags(e.currentTarget.value)}
                placeholder="tag1, tag2, tag3"
                class="skill-market-upload-input"
              />
            </div>

            <div class="skill-market-upload-actions">
              <button class="skill-market-detail-btn-ghost" onClick={props.onClose}>
                取消
              </button>
              <button
                class="skill-market-detail-btn-primary"
                disabled={props.uploading || extracting() || !name() || preparedFiles().length === 0}
                onClick={handleUpload}
              >
                {props.uploading ? "上传中..." : extracting() ? "解压中..." : "上传"}
              </button>
            </div>
          </div>
        </div>

        {/* 新加分类子弹框（叠加在上传弹框之上，复用同一套 CSS） */}
        <Show when={showAddCategory()}>
          <div class="skill-market-upload-overlay" onClick={() => setShowAddCategory(false)}>
            <div class="skill-market-upload-dialog" onClick={(e) => e.stopPropagation()}>
              <div class="skill-market-upload-header">
                <span class="skill-market-upload-title">新加分类</span>
                <button class="skill-market-upload-close" onClick={() => setShowAddCategory(false)}>✕</button>
              </div>

              <div style={{ display: "flex", "flex-direction": "column", gap: "16px" }}>
                <div class="skill-market-upload-field">
                  <span class="skill-market-upload-label">类型编码 *</span>
                  <input
                    type="text"
                    value={newCode()}
                    onInput={(e) => setNewCode(e.currentTarget.value)}
                    placeholder="custom-category"
                    class="skill-market-upload-input"
                  />
                </div>
                <div class="skill-market-upload-field">
                  <span class="skill-market-upload-label">类型名称 *</span>
                  <input
                    type="text"
                    value={newLabel()}
                    onInput={(e) => setNewLabel(e.currentTarget.value)}
                    placeholder="自定义分类"
                    class="skill-market-upload-input"
                  />
                </div>
                <div class="skill-market-upload-field">
                  <span class="skill-market-upload-label">排序</span>
                  <input
                    type="number"
                    value={newSortOrder()}
                    onInput={(e) => setNewSortOrder(e.currentTarget.value)}
                    placeholder="0"
                    class="skill-market-upload-input"
                  />
                </div>

                <div class="skill-market-upload-actions">
                  <button class="skill-market-detail-btn-ghost" onClick={() => setShowAddCategory(false)}>
                    取消
                  </button>
                  <button
                    class="skill-market-detail-btn-primary"
                    disabled={props.creatingCategory || !newCode().trim() || !newLabel().trim()}
                    onClick={handleCreateCategory}
                  >
                    {props.creatingCategory ? "保存中..." : "确认"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Show>
      </div>
    </Show>
  )
}
